import { cn } from "~/lib/utils";
import { TabSelect } from "~/components/TabSelect";
import { LeagueTableDataGrid } from "./LeagueTableDataGrid";
import { LeagueTableTab } from "~/modules/league_tables/types";
import * as Analytics from "./analytics";
import { ColumnSpanConfig } from "~/types";
import React, { useState } from "react";
import { LoginRequiredModal } from "~/modules/downloads/components/LoginRequiredModal";

// // // //

function LeagueTableCard(props: {
    title: string;
    loading: boolean;
    colSpans?: ColumnSpanConfig;
    children: React.ReactNode;
    analyticsTableName: string;
    headerChildren?: React.ReactNode;
}) {
    const {
        title,
        loading = false,
        children,
        analyticsTableName,
        headerChildren,
        colSpans = {
            sm: 12,
            md: 12,
            lg: 12,
            xl: 6,
            xxl: 6,
        },
    } = props;

    const smColSpan = colSpans.sm || 12;
    const mdColSpan = colSpans.md || 12;
    const lgColSpan = colSpans.lg || 12;
    const xlColSpan = colSpans.xl || 6;
    const xxlColSpan = colSpans.xxl || 6;

    return (
        <div
            data-testid={`league-table-card-${analyticsTableName}`}
            className={cn(
                "pt-5 bg-card rounded-lg shadow-xs border overflow-clip",
                "flex flex-col justify-between",
                `col-span-12 sm:col-span-${smColSpan} md:col-span-${mdColSpan} lg:col-span-${lgColSpan} xl:col-span-${xlColSpan} 2xl:col-span-${xxlColSpan}`,
                {
                    "opacity-70 pointer-events-none": loading,
                },
            )}
        >
            <div className="flex flex-col gap-2 mb-0 md:mb-4 md:flex-row md:justify-between md:items-center">
                <div
                    className={cn(
                        "flex w-full px-5",
                        "flex-col items-start",
                        "sm:flex-row sm:justify-between sm:items-center",
                        {
                            "gap-3": headerChildren !== null,
                        },
                    )}
                >
                    <p className="text-foreground text-xl leading">{title}</p>
                    {headerChildren && (
                        <React.Fragment>{headerChildren}</React.Fragment>
                    )}
                </div>
            </div>

            <div className="flex flex-col">{children}</div>
        </div>
    );
}

// // // //

export interface Props {
    cardTitle?: string;
    tabs: Array<LeagueTableTab>;
    colSpans?: ColumnSpanConfig;
    maxRows?: number;
    loading?: boolean;
    hideRankColumn?: boolean;
    headerChildren?: React.ReactNode;
    filterEmptyTabs?: boolean;
    analyticsTableName: string;
    onChangeQuery?: () => void;
    onChangeTab?: () => void;
}

export function LeagueTable(props: Props) {
    const {
        tabs,
        analyticsTableName,
        colSpans,
        maxRows = 10,
        loading = false,
        hideRankColumn,
        headerChildren,
        cardTitle = "Top Entities",
        onChangeQuery,
        onChangeTab = null,
        filterEmptyTabs = true,
    } = props;

    const [showLoginModal, setShowLoginModal] = useState(false);

    // Filter out tabs that have no data + assign test IDs
    const filteredTabs = !filterEmptyTabs
        ? tabs
        : tabs
              .filter((tab) => tab.data.rows && tab.data.rows.length > 0)
              .map((tab) => ({
                  ...tab,
                  testID: `league-table-tab-${tab.key}`,
              }));

    if (filteredTabs.length === 0) {
        return null;
    }

    return (
        <>
            <LeagueTableCard
                title={cardTitle}
                loading={loading}
                colSpans={colSpans}
                headerChildren={headerChildren}
                analyticsTableName={analyticsTableName}
            >
                <TabSelect
                    tabs={filteredTabs}
                    name={`league-table-${analyticsTableName}`}
                    tabStyle="tab"
                    className="mx-2 lg:mx-0"
                    onTabChange={({ prev, next }) => {
                        // Track tab change analytics
                        Analytics.trackLeagueTableTabChange({
                            prev: prev.key,
                            next: next.key,
                            table: analyticsTableName,
                        });

                        // Invoke onChangeTab if available
                        if (onChangeTab !== null) {
                            onChangeTab();
                        }
                    }}
                    onTabChangeLoginRequired={({ next }) => {
                        // Show login required modal
                        Analytics.trackLeagueTableLoginCtaOpen(next.key);
                        setShowLoginModal(true);
                    }}
                >
                    {(tab) => (
                        <LeagueTableDataGrid
                            tab={tab}
                            maxRows={maxRows}
                            analyticsTableName={analyticsTableName}
                            hideRankColumn={hideRankColumn}
                            onChangeQuery={onChangeQuery}
                        />
                    )}
                </TabSelect>
            </LeagueTableCard>

            <LoginRequiredModal
                allowScroll // NOTE - this is very important. When this is set to false, all the visualizations will resize and performance will crawl for a second.
                show={showLoginModal}
                onHide={() => {
                    setShowLoginModal(false);
                }}
                analytics={{
                    onConfirmed: () => {
                        Analytics.trackLeagueTableLoginCtaConfirmed();
                    },
                    onClose: () => {
                        Analytics.trackLeagueTableLoginCtaClose();
                    },
                }}
            />
        </>
    );
}
